import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { generateGradientsAndColors, generateGradients, applyGradients, formatLabel, NivoTextTheme } from "./utils";
import Tooltip from "./Tooltip";
import { CustomLabelsLayer } from "./Labels";
import { Tooltip as AntTooltip } from 'antd';
import { scaleLinear } from 'd3-scale';
import { interpolateRgb } from 'd3-interpolate';

const getLegendHeightBySize = (size) => {
    if (size === 'large') {
        return 25;
    }
    if (size === 'medium') {
        return 20;
    }
    if (size === 'small') {
        return 15;
    }
};

const getLegendSymbolSizeBySize = (size) => {
    if (size === 'large') {
        return 16;
    }
    if (size === 'medium') {
        return 12;
    }
    if (size === 'small') {
        return 10;
    }
};





const NivoBarChart = ({
    data, keys,
    customColors,
    bottomAxisLabel = '', leftAxisLabel = '', rotateLabels = 0,
    mode = 'grouped',
    precision = 1,
    postfix = '%',
    vertical = true,
    barPadding = 0.5,
    paddingBottom = 50,
    paddingLeft = 60,
    borderRadius = 0,
    showLegend = true,
    legendSize = 'small',
    bottomTickPadding = 5,
    skipInterval = 1,
    useBarShadow = true, shadowBlur = 3, shadowOffsetX = 2, shadowOffsetY = 2,
    useGradient = true, gradientFromOpacity = 1, gradientToOpacity = 0.6,
    shineIntensity = 0.2
}) => {
    const { colorMapping } = generateGradientsAndColors(keys, customColors);
    const colorsToUse = customColors ? customColors : colorMapping;

    const hasCustomLabels = data?.some?.(d => d.customLabel) ?? false;
    const layers = ['grid', 'axes', 'bars', 'markers', 'legends', 'annotations'];
    if (hasCustomLabels) {
        layers.push(({ bars }) => (
            <CustomLabelsLayer bars={bars} data={data} vertical={vertical} />
        ));
    }

    const CustomBar = ({ bar, ...rest }) => {
        const fillColor = colorsToUse[bar.data.id] || bar.color;
        const shadowId = `shadow-${bar.index}`;
        const gradientId = `gradient-${bar.index}`;

        const formattedValue = formatLabel({ value: bar.data.value }, precision, postfix);
        const colorScale = scaleLinear()
            .domain([0, 0.5, 1])
            .range([
                interpolateRgb(fillColor, '#ffffff')(shineIntensity),
                fillColor,
                interpolateRgb(fillColor, '#000000')(shineIntensity)
            ]);

        return (
            <AntTooltip title={`${bar.key}: ${formattedValue}`}>
                <g transform={`translate(${bar.x},${bar.y})`}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2={vertical === true ? '0' : '100%'} y2={vertical === true ? '100%' : '0%'}>
                            <stop offset="0%" stopColor={colorScale(0.5)} stopOpacity={gradientToOpacity} />
                            <stop offset="100%" stopColor={colorScale(1)} stopOpacity={gradientFromOpacity} />
                        </linearGradient>

                        <filter id={shadowId} x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur in="SourceAlpha" stdDeviation={shadowBlur} />
                            <feOffset dx={shadowOffsetX} dy={shadowOffsetY} result="offsetblur" />
                            <feComponentTransfer>
                                <feFuncA type="linear" slope="0.5" />
                            </feComponentTransfer>
                            <feMerge>
                                <feMergeNode />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>
                    <rect
                        width={bar.width}
                        height={bar.height}
                        rx={borderRadius}
                        ry={borderRadius}
                        filter={`url(#${shadowId})`}
                        {...rest}
                        fill={useGradient ? `url(#${gradientId})` : fillColor}
                    />
                </g>
            </AntTooltip>
        );
    };

    // Axis configuration
    const axisBottom = {
        tickSize: 5,
        tickPadding: bottomTickPadding,
        tickRotation: !vertical ? 0 : rotateLabels,
        legend: bottomAxisLabel,
        legendPosition: 'middle',
        legendOffset: 30,
        tickValues: data.map((d, i) => (i % skipInterval === 0 ? d.id : null)).filter(Boolean),
        format: !vertical ? () => '' : undefined,

    };

    const axisLeft = {
        tickSize: 5,
        tickPadding: 5,
        tickRotation: vertical ? 0 : rotateLabels,
        legend: leftAxisLabel,
        legendPosition: 'middle',
        legendOffset: -40,
        tickValues: 5,
        // For horizontal charts, wrap left-axis labels
    };

    return (
        <ResponsiveBar
            theme={NivoTextTheme}
            layers={layers}
            data={data}
            keys={keys}
            indexBy="id"
            margin={{ top: 10, right: showLegend ? 130 : 20, bottom: paddingBottom + 30, left: paddingLeft }}
            padding={barPadding}
            groupMode={mode}
            borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
            layout={vertical ? 'vertical' : 'horizontal'}
            axisTop={null}
            axisRight={null}
            axisBottom={axisBottom}
            axisLeft={axisLeft}
            gridYValues={5}
            innerPadding={5}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={'#DEE7EA'}
            label={(d) => formatLabel(d, precision, postfix)}
            legends={showLegend ? [
                {
                    dataFrom: 'keys',
                    anchor: 'top-right',
                    direction: 'column',
                    justify: false,
                    translateX: 120,
                    translateY: 0,
                    itemsSpacing: 5,
                    itemWidth: 100,
                    itemHeight: getLegendHeightBySize(legendSize),
                    symbolShape: 'circle',
                    itemDirection: 'left-to-right',
                    itemOpacity: 0.85,
                    itemFontSize: 10,
                    overflow: 'scroll',
                    symbolSize: getLegendSymbolSizeBySize(legendSize),
                    data: keys.map(key => ({
                        id: key,
                        label: key,
                        color: colorsToUse[key]
                    })),
                    effects: [
                        {
                            on: 'hover',
                            style: {
                                itemOpacity: 1
                            }
                        }
                    ]
                }
            ] : []}
            defs={generateGradients(keys, colorsToUse)}
            fill={applyGradients(keys)}
            animate={true}
            motionConfig={'wobbly'}
            motionStiffness={90}
            motionDamping={15}
            minValue="auto"
            maxValue="auto"
            borderRadius={borderRadius}
            tooltip={({ id, value }) => {
                const formattedValue = formatLabel({ value }, precision, postfix);
                return <Tooltip id={id} value={formattedValue} color={colorsToUse[id]} />;
            }}
            barComponent={useBarShadow ? CustomBar : undefined}
            colors={({ id, data }) => colorMapping[id] || data[`${id}Color`]}
        />
    );
};

export default NivoBarChart;
